// ADR-107 Decision 4.3 — remove a candidate from the bench.
//
// Mirrors the project Individuals menu (same wording, same date control as
// EndProjectModal): a bench allocation that has not started yet ("Joining") is
// CANCELLED — removed entirely; one that is already running ("Engaged") is ENDED
// on a date the operator picks (today by default, never before the start).
// radar-api decides the same way from the allocation's start date.

import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import Modal, { Field } from '../../common/Modal';
import { formatDateOnly } from './dates';
import { toPickerValue } from '../../../utils/dateOnly';

const UnassignDialog = ({ isOpen, candidate, submitting, onClose, onConfirm }) => {
  const allocation = candidate?.assignedEmployeeProject || null;
  const isFuture = Boolean(allocation?.isFuture);
  const startDate = toPickerValue(allocation?.startDate);

  const [endDate, setEndDate] = useState(() => dayjs());

  useEffect(() => {
    if (isOpen) {
      setEndDate(dayjs());
    }
  }, [isOpen]);

  if (!candidate) {
    return null;
  }

  const endDateValid = Boolean(endDate && endDate.isValid() && (!startDate || !endDate.isBefore(startDate, 'day')));

  return (
    <Modal
      isOpen={isOpen}
      onClose={submitting ? undefined : onClose}
      title={isFuture ? 'Cancel bench allocation' : 'End bench allocation'}
      onSubmit={() => onConfirm(isFuture ? {} : { endDate: endDate.format('YYYY-MM-DD') })}
      submitText={submitting ? (isFuture ? 'Cancelling…' : 'Ending…') : (isFuture ? 'Cancel allocation' : 'End allocation')}
      submitDisabled={submitting || (!isFuture && !endDateValid)}
      submitButtonProps={{
        sx: { backgroundColor: '#f59e0b', color: '#FFFFFF', '&:hover': { backgroundColor: '#d97706' } },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {isFuture ? (
          <Typography variant="body1">
            Cancel {candidate.fullName}&apos;s bench allocation starting {formatDateOnly(allocation.startDate)}?
            It has not started yet, so the allocation and its bench details are removed entirely — the same as
            &quot;Cancel Project Allocation&quot; on the project.
          </Typography>
        ) : (
          <>
            <Typography variant="body1">
              {candidate.fullName}&apos;s bench allocation is running
              {startDate ? ` since ${formatDateOnly(allocation.startDate)}` : ''}
              {allocation?.isLeaving ? ` and is scheduled to end on ${formatDateOnly(allocation.endDate)}` : ''}.
              The date below is the last day on the bench (today by default): the person shows as
              &quot;Leaving&quot; until then and the leave can still be cancelled; the bench details stay as
              history — the same rule as &quot;End Project Allocation&quot; on the project.
            </Typography>
            <Field>
              <Typography variant="subtitle1" gutterBottom>Bench End Date</Typography>
              <DatePicker
                value={endDate}
                onChange={(newValue) => {
                  if (newValue && typeof newValue.isValid === 'function' && newValue.isValid()) {
                    setEndDate(newValue);
                  } else if (newValue === null) {
                    setEndDate(null);
                  }
                }}
                minDate={startDate || undefined}
                format="DD/MM/YYYY"
                disabled={submitting}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'medium',
                    required: true,
                    inputProps: { placeholder: 'DD/MM/YYYY' },
                    helperText: !endDateValid && endDate ? `Cannot be before the allocation start (${formatDateOnly(allocation.startDate)})` : undefined,
                    error: Boolean(endDate) && !endDateValid,
                  },
                }}
              />
            </Field>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default UnassignDialog;
